import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useAuthStore } from "@/lib/auth-store";
import { useDelegationStore } from "@/lib/delegation-store";
import {
  loginUser,
  registerInitiate,
  registerComplete,
  socialLogin,
} from "@/lib/api/auth";
import type {
  LoginRequest,
  LoginResponse,
  RegistrationInitiateRequest,
  RegistrationInitiateResponse,
  RegistrationCompleteRequest,
  RegistrationCompleteResponse,
  SocialProvider,
} from "@/lib/api/auth";
import type { ApiRequestError } from "@/types/api";
import type { User } from "@/types/auth";

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** Store auth state without navigation. */
function useSetAuth() {
  const { setAuth, setUser } = useAuthStore();

  return (data: { userId?: string; user?: { id: string } }) => {
    setAuth();
    const id = data.userId ?? data.user?.id;
    if (id) {
      setUser({ id } as User);
    }
  };
}

/** Store auth state and navigate to /home. */
function useAuthSuccess() {
  const applyAuth = useSetAuth();
  const router = useRouter();

  return (data: { userId?: string; user?: { id: string } }) => {
    applyAuth(data);
    router.push("/home");
  };
}

// ===========================================================================
// Page-level hooks (redirect to /home on success)
// ===========================================================================

export function useLogin() {
  const onSuccess = useAuthSuccess();

  return useMutation<LoginResponse, ApiRequestError, LoginRequest>({
    mutationFn: loginUser,
    onSuccess,
  });
}

export function useRegisterInitiate() {
  return useMutation<
    RegistrationInitiateResponse,
    ApiRequestError,
    RegistrationInitiateRequest
  >({
    mutationFn: registerInitiate,
  });
}

export function useRegisterComplete() {
  const onSuccess = useAuthSuccess();

  return useMutation<
    RegistrationCompleteResponse,
    ApiRequestError,
    RegistrationCompleteRequest
  >({
    mutationFn: registerComplete,
    onSuccess,
  });
}

export function useSocialLogin() {
  const onSuccess = useAuthSuccess();

  return useMutation<
    LoginResponse,
    ApiRequestError,
    { provider: SocialProvider; token: string }
  >({
    mutationFn: ({ provider, token }) => socialLogin(provider, token),
    onSuccess,
  });
}

// ===========================================================================
// Inline hooks (for auth dialog — stay on current page)
// ===========================================================================

export function useInlineLogin() {
  const applyAuth = useSetAuth();
  const queryClient = useQueryClient();

  return useMutation<LoginResponse, ApiRequestError, LoginRequest>({
    mutationFn: loginUser,
    onSuccess: (data) => {
      applyAuth(data);
      queryClient.invalidateQueries();
    },
  });
}

export function useInlineRegisterComplete() {
  const applyAuth = useSetAuth();
  const queryClient = useQueryClient();

  return useMutation<
    RegistrationCompleteResponse,
    ApiRequestError,
    RegistrationCompleteRequest
  >({
    mutationFn: registerComplete,
    onSuccess: (data) => {
      applyAuth(data);
      queryClient.invalidateQueries();
    },
  });
}

export function useInlineSocialLogin() {
  const applyAuth = useSetAuth();
  const queryClient = useQueryClient();

  return useMutation<
    LoginResponse,
    ApiRequestError,
    { provider: SocialProvider; token: string }
  >({
    mutationFn: ({ provider, token }) => socialLogin(provider, token),
    onSuccess: (data) => {
      applyAuth(data);
      queryClient.invalidateQueries();
    },
  });
}

// ===========================================================================
// Logout
// ===========================================================================

export function useLogout() {
  const { logout } = useAuthStore();
  const { clearDelegation } = useDelegationStore();
  const queryClient = useQueryClient();
  const router = useRouter();

  return async () => {
    const logoutRequest = fetch("/api/token/logout", {
      method: "POST",
      credentials: "include",
      cache: "no-store",
      keepalive: true,
    }).catch(() => {});

    // Clear local state immediately — never block on the network.
    queryClient.clear();
    clearDelegation();
    logout();
    router.replace("/login");

    await logoutRequest;
  };
}
